import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import StatusBadge from './components/StatusBadge';

const LegalDocumentDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState(false);

  const fetchDocument = useCallback(async () => {
    try {
      const response = await api.get(`/api/super-admin/legal-documents/${id}`);
      setDocument(response.data.data);
    } catch (error) {
      console.error('Error fetching document:', error);
      alert('Failed to fetch document');
      navigate('/super-admin/legal-documents');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      terms_of_service: 'Terms of Service',
      privacy_policy: 'Privacy Policy',
      refund_policy: 'Refund Policy',
      cookie_policy: 'Cookie Policy',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-3 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-sm font-medium text-gray-500">Retrieving document details...</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-base font-bold text-gray-900 mb-1">Document Not Found</p>
          <p className="text-xs text-gray-500 mb-6">The requested document record does not exist or has been deleted.</p>
          <button
            onClick={() => navigate('/super-admin/legal-documents')}
            className="w-full px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs rounded-xl transition-colors"
          >
            Return to Documents List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 antialiased p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header Bar */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div>
            <button
              onClick={() => navigate('/super-admin/legal-documents')}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors flex items-center gap-1 mb-2 focus:outline-none"
            >
              &larr; Back to Legal Management
            </button>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
              Document Record Details
            </h1>
          </div>
        </div>

        {/* Main Document Details Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          
          {/* Document Title Header */}
          <div className="bg-slate-900 px-6 py-6 text-white border-b border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="font-mono text-xs font-bold bg-slate-800 text-indigo-300 px-2.5 py-0.5 rounded border border-slate-700">
                  v{document.version}
                </span>
                <StatusBadge status={document.status} />
              </div>
              <h2 className="text-2xl font-extrabold text-white">{document.title}</h2>
              <p className="text-xs text-slate-400 mt-1 font-medium">
                Scope: <span className="text-indigo-300">{getDocumentTypeLabel(document.documentType)}</span>
              </p>
            </div>

            <div className="flex flex-wrap sm:flex-col items-start sm:items-end gap-2">
              <button
                onClick={() => navigate(`/super-admin/legal-documents/versions/${document.documentType}`)}
                className="px-3.5 py-2 text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-indigo-300 rounded-lg border border-slate-700 transition-colors focus:outline-none"
              >
                View Version History
              </button>
              {document.status === 'draft' && (
                <button
                  onClick={() => navigate(`/super-admin/legal-documents/edit/${document._id}`)}
                  className="px-3.5 py-2 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors focus:outline-none"
                >
                  Edit Document
                </button>
              )}
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-6">

            {/* Metadata Summary Grid */}
            <div>
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">
                Audit & Metadata Overview
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200/80 text-xs sm:text-sm">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Created On</div>
                  <div className="font-medium text-gray-900 mt-1">{formatDate(document.createdAt)}</div>
                  <div className="text-xs text-gray-400 mt-0.5">by {document.createdBy?.name || 'System Admin'}</div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Modified</div>
                  <div className="font-medium text-gray-900 mt-1">{formatDate(document.updatedAt)}</div>
                </div>

                {document.effectiveDate && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Effective Date</div>
                    <div className="font-medium text-gray-900 mt-1">{formatDate(document.effectiveDate)}</div>
                  </div>
                )}

                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Word Volume</div>
                  <div className="font-medium font-mono text-gray-900 mt-1">{document.metadata?.wordCount || 0} words</div>
                </div>
              </div>
            </div>

            {/* Change Log Commentary */}
            {document.changeLog && (
              <div>
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">
                  Revision Commentary
                </h3>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs sm:text-sm text-amber-900 leading-relaxed">
                  <span className="font-semibold block mb-1">Change Log:</span>
                  {document.changeLog}
                </div>
              </div>
            )}

            {/* Document Content View */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Document Content Preview
                </h3>
                <button
                  onClick={() => setShowRaw(!showRaw)}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors focus:outline-none"
                >
                  {showRaw ? 'Switch to Rendered Mode' : 'Switch to Raw HTML'}
                </button>
              </div>

              <div className="p-6 border border-gray-200 rounded-2xl bg-white max-h-[600px] overflow-y-auto shadow-inner">
                {showRaw ? (
                  <pre className="text-xs sm:text-sm whitespace-pre-wrap font-mono text-slate-800 bg-slate-50 p-4 rounded-xl border border-slate-200 leading-relaxed">
                    {document.content}
                  </pre>
                ) : (
                  <div 
                    className="prose prose-slate max-w-none 
                      prose-headings:font-bold prose-headings:text-gray-900 prose-headings:tracking-tight 
                      prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-3 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-100
                      prose-h3:text-lg prose-h3:mt-4 prose-h3:mb-2
                      prose-p:text-gray-600 prose-p:leading-relaxed prose-p:mb-4
                      prose-a:text-indigo-600 prose-a:font-medium hover:prose-a:text-indigo-700
                      prose-strong:text-gray-900 prose-strong:font-semibold
                      prose-ul:text-gray-600 prose-ul:my-4 prose-ul:list-disc
                      prose-ol:text-gray-600 prose-ol:my-4 prose-ol:list-decimal
                      prose-li:my-1 text-sm"
                    dangerouslySetInnerHTML={{ __html: document.content }}
                  />
                )}
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default LegalDocumentDetails;